/**
 * Chilean supplier-invoice math: Neto + ILA/IABA + IVA 19% + flete.
 * ILA is an additional tax (not part of the IVA base). Total = neto + ila + iva.
 */

export const IVA_RATE_CL = 0.19;

export const ILA_RATE_OPTIONS = [
    { value: 0, label: 'Sin ILA' },
    { value: 0.18, label: '18% (IABA / cerveza)' },
    { value: 0.205, label: '20,5% (vinos / cerveza)' },
    { value: 0.315, label: '31,5% (destilados)' },
];

/** Name/category hints for alcohol. Never applied to flete or unmarked grocery lines. */
const ILA_NAME_HINT = /\b(pisco|vodka|whisky|whiskey|gin|ron|tequila|licor(?:es)?|cerveza|vino|vinos|champagne|champaña|sidra|destilad\w*|alcohol|mistral|capel|bauz[aá]|fernet|anisado|aguardiente|pisco)\b/i;

/** Cecina and similar foods can include "cerveza" as a flavor, not as alcohol. */
const ILA_FOOD_EXCLUDE = /\b(salchich\w*|fiambre|cecina|jam[oó]n|hamburguesa|mortadela|chorizo|vienesa|longaniza|prieta|pat[eé]|arroz)\b/i;

export function suggestsIla(product = {}) {
    const text = [
        product.nombre,
        product.categoria_nombre,
        product.subcategoria_nombre,
        product.marca_nombre,
    ].filter(Boolean).join(' ');
    if (ILA_FOOD_EXCLUDE.test(text)) return false;
    return ILA_NAME_HINT.test(text);
}

export function roundCLP(n) {
    return Math.round(Number(n) || 0);
}

export function roundUnitCost(n) {
    return Math.round((Number(n) || 0) * 10000) / 10000;
}

export function lineNetoOf(item) {
    if (item == null) return 0;
    if (item.lineTotal != null && item.lineTotal !== '') return roundCLP(item.lineTotal);
    return roundCLP((Number(item.quantity) || 0) * (Number(item.cost) || 0));
}

/** Largest-remainder allocation so integer pesos sum exactly to `total`. */
export function allocateInteger(total, weights) {
    const t = roundCLP(total);
    const w = (weights || []).map((x) => Math.max(0, Number(x) || 0));
    const sumW = w.reduce((a, b) => a + b, 0);
    if (t === 0 || w.length === 0) return w.map(() => 0);
    if (sumW <= 0) return w.map(() => 0);
    const exact = w.map((x) => (x / sumW) * t);
    const floors = exact.map((x) => Math.floor(x));
    let remain = t - floors.reduce((a, b) => a + b, 0);
    const order = exact
        .map((x, i) => ({ i, frac: x - floors[i] }))
        .sort((a, b) => b.frac - a.frac || a.i - b.i);
    const out = floors.slice();
    for (let k = 0; k < remain; k += 1) {
        out[order[k % order.length].i] += 1;
    }
    return out;
}

function emptyLanded(items, lineNetos) {
    return (items || []).map((it, i) => {
        const qty = Number(it.quantity) || 0;
        const lineLanded = lineNetos[i] || 0;
        return {
            id: it.id,
            lineNeto: lineLanded,
            fleteShare: 0,
            ilaShare: 0,
            ivaShare: 0,
            lineLanded,
            unit: qty > 0 ? roundUnitCost(lineLanded / qty) : Number(it.cost) || 0,
        };
    });
}

/**
 * @param {Array} items cart lines with quantity, cost, lineTotal, afecta_ila
 * @param {{ precioTipo: string, flete?: number, ilaRate?: number, ilaAmount?: number|null, ivaAmount?: number|null }} extras
 */
export function buildInvoiceBreakdown(items = [], extras = {}) {
    const precioTipo = String(extras.precioTipo || 'bruto').toLowerCase();
    const lineNetos = items.map(lineNetoOf);
    const productNeto = lineNetos.reduce((a, b) => a + b, 0);
    const flete = roundCLP(extras.flete);
    const ilaRate = Number(extras.ilaRate) || 0;
    const hasIlaInput = extras.ilaAmount != null && extras.ilaAmount !== '';
    const hasIvaInput = extras.ivaAmount != null && extras.ivaAmount !== '';
    const hasExtras = flete > 0 || ilaRate > 0 || (hasIlaInput && roundCLP(extras.ilaAmount) > 0);
    const chilean = precioTipo === 'neto' || hasExtras;

    if (!chilean) {
        const total = productNeto;
        const neto = roundCLP(total / (1 + IVA_RATE_CL));
        const iva = total - neto;
        const landed = emptyLanded(items, lineNetos);
        return {
            chilean: false,
            productNeto,
            flete: 0,
            neto,
            ila: 0,
            iva,
            total,
            alcoholNeto: 0,
            ilaProductCount: 0,
            landed,
            landedById: Object.fromEntries(landed.map((l) => [l.id, l])),
        };
    }

    const neto = productNeto + flete;
    const alcoholWeights = items.map((it, i) => (it.afecta_ila === true ? lineNetos[i] : 0));
    const alcoholNeto = alcoholWeights.reduce((a, b) => a + b, 0);
    const ila = hasIlaInput
        ? roundCLP(extras.ilaAmount)
        : roundCLP(alcoholNeto * ilaRate);
    const iva = hasIvaInput
        ? roundCLP(extras.ivaAmount)
        : roundCLP(neto * IVA_RATE_CL);
    const total = neto + ila + iva;

    const fleteShares = allocateInteger(flete, lineNetos);
    const ilaShares = allocateInteger(ila, alcoholWeights);
    const ivaBases = lineNetos.map((n, i) => n + fleteShares[i]);
    const ivaShares = allocateInteger(iva, ivaBases);

    const landed = items.map((it, i) => {
        const lineLanded = lineNetos[i] + fleteShares[i] + ilaShares[i] + ivaShares[i];
        const qty = Number(it.quantity) || 0;
        return {
            id: it.id,
            lineNeto: lineNetos[i],
            fleteShare: fleteShares[i],
            ilaShare: ilaShares[i],
            ivaShare: ivaShares[i],
            lineLanded,
            unit: qty > 0 ? roundUnitCost(lineLanded / qty) : 0,
        };
    });

    return {
        chilean: true,
        productNeto,
        flete,
        neto,
        ila,
        iva,
        total,
        alcoholNeto,
        ilaProductCount: alcoholWeights.filter((w) => w > 0).length,
        landed,
        landedById: Object.fromEntries(landed.map((l) => [l.id, l])),
    };
}
